<template>
  <!-- CLOSED -->
  <Container
    tag="nav"
    indent="level-1"
    class="
      fixed bottom-0 left-0 z-30 flex w-full items-center justify-between bg-violet-500 py-5 text-violet-text
    "
    role="navigation"
  >
    <DynLink
      :to="auth.homepage"
      aria-label="Dashboard"
      class="group ring-offset-8 focus:outline-none focus-visible:ring-2 focus-visible:ring-focusTeacher"
    >
      <IconLogo
        class="h-6.5 [&_path]:transition-colors group-hover:[&_path]:!fill-peach-500"
        type="white"
      />
      <IconTrialLogo v-if="auth.staff?.organisation?.is_trial" class="text-white" />
    </DynLink>

    <button
      class="group -mr-4 h-9 px-4 py-1 text-white transition-opacity"
      type="button"
      @click="isOpen = true"
    >
      <IconMenuBurger class="h-full text-white" />
    </button>
  </Container>

  <!-- OPEN -->
  <Container
    v-if="auth.user"
    tag="nav"
    class="
      fixed inset-0 z-30 flex w-full flex-col items-center justify-between bg-violet-500 py-5 text-violet-text
      transition-all ease-[cubic-bezier(0.12,0.85,0,0.98)]
      md:hidden
    "
    role="navigation"
    :aria-expanded="isOpen"
    :aria-label="isOpen ? 'Mobile Navigation is open' : 'hidden'"
    :class="
      isOpen
        ? 'top-0 duration-500 ease-[cubic-bezier(0.12,0.85,0,0.98)]'
        : 'top-full duration-300'
    "
  >
    <!-- TOP -->
    <div class="flex w-full items-center justify-between">
      <DynLink
        class="group flex w-fit items-center space-x-2 text-white focus:outline-none"
        aria-label="to administration section of page"
        :to="{ name: 'AdminIndex' }"
      >
        <span
          class="
            size-10 shrink-0 rounded-full bg-white p-2.5 text-white transition-all
            focus:outline-none
            group-hover:bg-violet-50
            group-focus-visible:bg-violet-50 group-focus-visible:ring-2 group-focus-visible:ring-focusTeacher
            group-focus-visible:ring-offset-2
          "
        >
          <IconSetting class="w-full text-violet-500 transition-all" />
        </span>
        <span
          class="font-ingra text-lg font-semibold group-hover:text-violet-50 group-focus-visible:underline"
        >
          Verwalten
        </span>
      </DynLink>
      <button
        class="group flex w-fit items-center space-x-2 text-white focus:outline-none"
        type="button"
        title="Logout"
        aria-label="logout of the platform"
        @click="auth.logout()"
      >
        <span
          class="
            size-10 shrink-0 rounded-full bg-white p-2.5 text-white transition-all
            focus:outline-none
            group-hover:bg-violet-50
            group-focus-visible:bg-violet-50 group-focus-visible:ring-2 group-focus-visible:ring-focusTeacher
            group-focus-visible:ring-offset-2
          "
        >
          <IconUser class="w-full text-violet-500 transition-all" />
        </span>
        <span
          class="font-ingra text-lg font-semibold group-hover:text-violet-50 group-focus-visible:underline"
        >
          abmelden
        </span>
      </button>
    </div>
    <div class="w-full">
      <div class="mb-[12svh] flex h-fit w-full flex-col items-center space-y-12">
        <UiSelect
          v-if="auth.staff!.school_classes.length > 1"
          v-model="auth.schoolClass"
          :entries="auth.staff!.school_classes"
          :labler="({ name }) => `Klasse ${name}`"
          button-class="bg-transparent text-white hover:bg-violet-400"
        />

        <menu class="flex w-full flex-col gap-2 text-white">
          <li
            v-for="{ page } in cms.globals?.main?.tree"
            :key="page.id"
            class="
              flex w-full flex-wrap text-center text-white
              hover:text-violet-50
              has-[:focus-visible]:border-surface-500 has-[:focus-visible]:ring-2
              has-[:focus-visible]:ring-violet-50
            "
          >
            <!-- not current page -->
            <DynLink
              :to="page.url"
              :aria-label="`go to ${page.title}`"
              class="
                w-full text-center text-4xl font-bold leading-relaxed
                focus:outline-none
                link-exact:hidden link-exact:[&_+_button]:!block
              "
            >
              {{ page.title }}
            </DynLink>
            <!-- current page -->
            <button
              type="button"
              :aria-label="`you are on ${page.title}. This is the current page. Press to close mobile navigation`"
              class="hidden w-full text-center text-4xl font-bold leading-relaxed focus:outline-none"
              @click="isOpen = false"
            >
              {{ page.title }}
            </button>
          </li>
        </menu>
      </div>
      <div class="flex w-full items-center justify-between">
        <DynLink
          to="/lp"
          aria-label="Dashboard"
          class="group ring-offset-8 focus:outline-none focus-visible:ring-2 focus-visible:ring-focusTeacher"
        >
          <IconLogo
            class="h-7 [&_path]:transition-colors group-hover:[&_path]:!fill-peach-500"
            type="white"
          />
          <IconTrialLogo v-if="auth.staff?.organisation?.is_trial" class="text-white" />
        </DynLink>

        <button
          class="
            group -mr-4 flex h-9 items-center px-4 py-1 text-white transition-opacity
            hover:text-violet-100
          "
          type="button"
          @click="isOpen = false"
        >
          <span class="mr-4 font-ingra text-base font-semibold transition-all group-hover:mr-3">
            {{ $t('general.close') }}
          </span>
          <IconCross class="h-full group-hover:scale-y-90" />
        </button>
      </div>
    </div>
  </Container>
</template>

<script lang="ts" setup>
const cms = useCmsStore()
const auth = useAuthStore()
const isOpen = ref(false)
const route = useRoute()

watch(
  () => route.path,
  () => {
    isOpen.value = false
  },
)
</script>
